import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { newHttpService } from '../services/new-http-service'
import { useCachedFetch } from '../hooks/use-cached-fetch'
import { Spinner } from '../cmps/util-cmps/spinner'
import { AlertEventFeed } from '../cmps/alert-events/alert-event-feed'
import { useDraggable } from '../hooks/use-draggable'
import { useEscapeKey } from '../hooks/use-escape-key'
import { useScrollLock } from '../hooks/use-scroll-lock'
import { Backlog } from '../cmps/util-cmps/backlog'

interface UnhealthyAuditControl {
    audit_control_id: number
    company_name: string
    entity_name: string
    system_name: string | null
    objective_name: string | null
    year: number
    stage: string | null
    last_run: string | null
    latest_successful_run: string | null
    error: string | null
}

interface NoHistoryControl {
    audit_control_id: number
    company_name: string
    entity_name: string
    system_name: string | null
    objective_name: string | null
    year: number
}

interface ErrorFinding {
    audit_control_id: number
    company_name: string
    entity_name: string
    system_name: string | null
    objective_name: string | null
    year: number
    error: string | null
}

interface HealthReportData {
    all_acs_count: number
    unhealthy_acs_count: number
    generated_at: string
    items: UnhealthyAuditControl[]
    error_findings: ErrorFinding[]
}

interface NoHistoryData {
    total_controls_count: number
    no_history_count: number
    generated_at: string
    items: NoHistoryControl[]
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatRelative(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 2) return 'Just now'
    if (mins < 60) return `${mins}m ago`
    const h = Math.floor(diff / 3600000)
    if (h < 24) return `${h}h ago`
    const d = Math.floor(h / 24)
    return d === 1 ? 'Yesterday' : `${d}d ago`
}

// ── Detail Modal ─────────────────────────────────────────────────────────────

interface HrDetailModalProps {
    title: string
    onClose: () => void
    children: React.ReactNode
}

const HrDetailModal: React.FC<HrDetailModalProps> = ({ title, onClose, children }) => {
    useEscapeKey(onClose)
    useScrollLock()
    const { modalRef, onHeaderMouseDown } = useDraggable<HTMLElement>()

    return <>
        <section
            ref={modalRef}
            className="form-modal hr-detail-modal"
            style={{ zIndex: 300 }}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={e => e.stopPropagation()}
        >
            <header onMouseDown={onHeaderMouseDown}>
                <h2>{title}</h2>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>
            <div className="hr-modal-scroll">
                {children}
            </div>
        </section>
        <Backlog zIndex={299} onCloseBacklog={onClose} />
    </>
}

// ── Health Report Card ────────────────────────────────────────────────────────

interface HealthReportCardProps {
    data: HealthReportData | undefined
    isLoading: boolean
    hasError: boolean
    isRefreshing: boolean
    onRefresh: () => void | Promise<void>
}

const HealthReportCard: React.FC<HealthReportCardProps> = ({ data, isLoading, hasError, isRefreshing, onRefresh }) => {
    const [modalOpen, setModalOpen] = useState(false)

    const titleNode = <div className="hr-card-title-group"><span className="hr-card-icon">🩺</span><span className="hr-card-title">Health Report</span></div>

    if (isLoading) return (
        <div className="hr-card">
            <div className="hr-card-header">{titleNode}</div>
            <span className="hr-card-loading">Loading...</span>
        </div>
    )
    if (hasError || !data) return (
        <div className="hr-card hr-card--warn">
            <div className="hr-card-header">{titleNode}</div>
            <span className="hr-card-error">Failed to load report.</span>
        </div>
    )

    const isOk = data.unhealthy_acs_count === 0
    const pct = data.all_acs_count > 0
        ? Math.round(data.unhealthy_acs_count / data.all_acs_count * 100)
        : 0

    return (
        <>
        <div
            className={`hr-card hr-card--button ${isOk ? 'hr-card--ok' : 'hr-card--warn'}`}
            role="button"
            tabIndex={0}
            onClick={() => setModalOpen(true)}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setModalOpen(true)
                }
            }}
        >
            <div className="hr-card-header">
                {titleNode}
                <div className="hr-card-header-actions">
                    <span className="hr-card-meta">Generated {formatRelative(data.generated_at)}</span>
                    <button
                        type="button"
                        className="hr-card-refresh"
                        disabled={isRefreshing}
                        onClick={e => {
                            e.stopPropagation()
                            onRefresh()
                        }}
                        aria-label="Refresh health report"
                        title={isRefreshing ? 'Refreshing health report' : 'Refresh health report'}
                    >
                        {isRefreshing ? <Spinner isSmall={true} className="hr-card-refresh-spinner" /> : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="23 4 23 10 17 10" />
                                <path d="M20.49 15A9 9 0 1 1 21 12" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            <span className="hr-card-kpi-label">Unhealthy Audit Controls</span>
            <span className="hr-card-explanation">Last successful run was over 24 hours ago or the control has never run.</span>
            <div className="hr-card-numbers">
                <span className={`hr-card-count ${!isOk ? 'hr-card-count--alert' : ''}`}>
                    {data.unhealthy_acs_count}
                </span>
                {!isOk && <span className="hr-card-pct">{pct}%</span>}
            </div>
            <span className="hr-card-total">out of {data.all_acs_count} total Audit Controls</span>

            {modalOpen && (
                <HrDetailModal title="Unhealthy Audit Controls" onClose={() => setModalOpen(false)}>
                    <table className="hr-detail-table">
                        <thead>
                            <tr>
                                <th className="hr-detail-th">AC ID</th>
                                <th className="hr-detail-th">Entity</th>
                                <th className="hr-detail-th">System</th>
                                <th className="hr-detail-th">Objective</th>
                                <th className="hr-detail-th">Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map(ac => (
                                <tr key={ac.audit_control_id} className="hr-detail-tr">
                                    <td className="hr-detail-td hr-detail-td--id">
                                        <button
                                            className="hr-ac-id-link"
                                            onClick={() => window.open(`/companies/audit-controls?company=${encodeURIComponent(ac.company_name.toLowerCase())}&expandedId=${ac.audit_control_id}`, '_blank')}
                                        >
                                            {ac.audit_control_id}
                                        </button>
                                    </td>
                                    <td className="hr-detail-td">{ac.entity_name}</td>
                                    <td className="hr-detail-td hr-detail-td--mono">{ac.system_name ?? '—'}</td>
                                    <td className="hr-detail-td">{ac.objective_name ?? '—'}</td>
                                    <td className="hr-detail-td hr-detail-td--error" title={ac.error ?? ''}>{ac.error ?? '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </HrDetailModal>
            )}
        </div>
        </>
    )
}

// ── Error Findings Card ───────────────────────────────────────────────────────

interface ErrorFindingsCardProps {
    data: HealthReportData | undefined
    isLoading: boolean
    hasError: boolean
    isRefreshing: boolean
    onRefresh: () => void | Promise<void>
}

const ErrorFindingsCard: React.FC<ErrorFindingsCardProps> = ({ data, isLoading, hasError, isRefreshing, onRefresh }) => {
    const [modalOpen, setModalOpen] = useState(false)

    const titleNode = <div className="hr-card-title-group"><span className="hr-card-icon">⚠️</span><span className="hr-card-title">Rule-Manager Error Findings</span></div>

    if (isLoading) return (
        <div className="hr-card">
            <div className="hr-card-header">{titleNode}</div>
            <span className="hr-card-loading">Loading...</span>
        </div>
    )
    if (hasError || !data) return (
        <div className="hr-card hr-card--warn">
            <div className="hr-card-header">{titleNode}</div>
            <span className="hr-card-error">Failed to load report.</span>
        </div>
    )

    const hasIssues = data.error_findings.length > 0

    return (
        <>
        <div
            className={`hr-card hr-card--button ${hasIssues ? 'hr-card--warn' : 'hr-card--ok'}`}
            role="button"
            tabIndex={0}
            onClick={() => setModalOpen(true)}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setModalOpen(true)
                }
            }}
        >
            <div className="hr-card-header">
                {titleNode}
                <div className="hr-card-header-actions">
                    <span className="hr-card-meta">Generated {formatRelative(data.generated_at)}</span>
                    <button
                        type="button"
                        className="hr-card-refresh"
                        disabled={isRefreshing}
                        onClick={e => {
                            e.stopPropagation()
                            onRefresh()
                        }}
                        aria-label="Refresh error findings report"
                        title={isRefreshing ? 'Refreshing error findings report' : 'Refresh error findings report'}
                    >
                        {isRefreshing ? <Spinner isSmall={true} className="hr-card-refresh-spinner" /> : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="23 4 23 10 17 10" />
                                <path d="M20.49 15A9 9 0 1 1 21 12" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            <span className="hr-card-kpi-label">Error Findings</span>
            <span className="hr-card-explanation">Audit controls where the rule engine's latest run returned error findings — the ETL ran, but the results contain flagged errors.</span>
            <div className="hr-card-numbers">
                <span className={`hr-card-count ${hasIssues ? 'hr-card-count--alert' : ''}`}>
                    {data.error_findings.length}
                </span>
            </div>

            {modalOpen && (
                <HrDetailModal title="Rule-Manager Error Findings" onClose={() => setModalOpen(false)}>
                    <table className="hr-detail-table">
                        <thead>
                            <tr>
                                <th className="hr-detail-th">AC ID</th>
                                <th className="hr-detail-th">Company</th>
                                <th className="hr-detail-th">Entity</th>
                                <th className="hr-detail-th">System</th>
                                <th className="hr-detail-th">Objective</th>
                                <th className="hr-detail-th">Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.error_findings.map(ef => (
                                <tr key={ef.audit_control_id} className="hr-detail-tr">
                                    <td className="hr-detail-td hr-detail-td--id">
                                        <button
                                            className="hr-ac-id-link"
                                            onClick={() => window.open(`/companies/audit-controls?company=${encodeURIComponent(ef.company_name.toLowerCase())}&expandedId=${ef.audit_control_id}`, '_blank')}
                                        >
                                            {ef.audit_control_id}
                                        </button>
                                    </td>
                                    <td className="hr-detail-td hr-detail-td--company">{ef.company_name}</td>
                                    <td className="hr-detail-td">{ef.entity_name}</td>
                                    <td className="hr-detail-td hr-detail-td--mono">{ef.system_name ?? '—'}</td>
                                    <td className="hr-detail-td">{ef.objective_name ?? '—'}</td>
                                    <td className="hr-detail-td hr-detail-td--error" title={ef.error ?? ''}>{ef.error ?? '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </HrDetailModal>
            )}
        </div>
        </>
    )
}

// ── No History Report Card ────────────────────────────────────────────────────

interface NoHistoryCardProps {
    data: NoHistoryData | undefined
    isLoading: boolean
    hasError: boolean
    isRefreshing: boolean
    onRefresh: () => void | Promise<void>
}

const NoHistoryCard: React.FC<NoHistoryCardProps> = ({ data, isLoading, hasError, isRefreshing, onRefresh }) => {
    const [modalOpen, setModalOpen] = useState(false)

    const titleNode = <div className="hr-card-title-group"><span className="hr-card-icon">🗓️</span><span className="hr-card-title">No History Report</span></div>

    if (isLoading) return (
        <div className="hr-card">
            <div className="hr-card-header">{titleNode}</div>
            <span className="hr-card-loading">Loading...</span>
        </div>
    )
    if (hasError || !data) return (
        <div className="hr-card hr-card--warn">
            <div className="hr-card-header">{titleNode}</div>
            <span className="hr-card-error">Failed to load report.</span>
        </div>
    )

    const isOk = data.no_history_count === 0
    const pct = data.total_controls_count > 0
        ? Math.round(data.no_history_count / data.total_controls_count * 100)
        : 0

    return (
        <>
        <div
            className={`hr-card hr-card--button ${isOk ? 'hr-card--ok' : 'hr-card--warn'}`}
            role="button"
            tabIndex={0}
            onClick={() => setModalOpen(true)}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setModalOpen(true)
                }
            }}
        >
            <div className="hr-card-header">
                {titleNode}
                <div className="hr-card-header-actions">
                    <span className="hr-card-meta">Generated {formatRelative(data.generated_at)}</span>
                    <button
                        type="button"
                        className="hr-card-refresh"
                        disabled={isRefreshing}
                        onClick={e => {
                            e.stopPropagation()
                            onRefresh()
                        }}
                        aria-label="Refresh no history report"
                        title={isRefreshing ? 'Refreshing no history report' : 'Refresh no history report'}
                    >
                        {isRefreshing ? <Spinner isSmall={true} className="hr-card-refresh-spinner" /> : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="23 4 23 10 17 10" />
                                <path d="M20.49 15A9 9 0 1 1 21 12" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            <span className="hr-card-kpi-label">Audit Controls without history</span>
            <span className="hr-card-explanation">Audit controls that have been set up but have never produced any run history.</span>
            <div className="hr-card-numbers">
                <span className={`hr-card-count ${!isOk ? 'hr-card-count--alert' : ''}`}>
                    {data.no_history_count}
                </span>
                {pct < 100 && <span className="hr-card-pct">{pct}%</span>}
            </div>

            {modalOpen && (
                <HrDetailModal title="Audit Controls without History" onClose={() => setModalOpen(false)}>
                    <table className="hr-detail-table">
                        <thead>
                            <tr>
                                <th className="hr-detail-th">AC ID</th>
                                <th className="hr-detail-th">Entity</th>
                                <th className="hr-detail-th">System</th>
                                <th className="hr-detail-th">Objective</th>
                                <th className="hr-detail-th">Year</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map(ac => (
                                <tr key={ac.audit_control_id} className="hr-detail-tr">
                                    <td className="hr-detail-td hr-detail-td--id">
                                        <button
                                            className="hr-ac-id-link"
                                            onClick={() => window.open(`/companies/audit-controls?company=${encodeURIComponent(ac.company_name.toLowerCase())}&expandedId=${ac.audit_control_id}`, '_blank')}
                                        >
                                            {ac.audit_control_id}
                                        </button>
                                    </td>
                                    <td className="hr-detail-td">{ac.entity_name}</td>
                                    <td className="hr-detail-td hr-detail-td--mono">{ac.system_name ?? '—'}</td>
                                    <td className="hr-detail-td">{ac.objective_name ?? '—'}</td>
                                    <td className="hr-detail-td hr-detail-td--mono">{ac.year}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </HrDetailModal>
            )}
        </div>
        </>
    )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const HealthReportPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const highlightPair = searchParams.get('pair') ?? undefined

    const { data: healthData, isLoading: healthLoading, hasError: healthError, refetch: refetchHealth } = useCachedFetch(
        'health-report',
        () => newHttpService.get('/api/backoffice/health-report') as Promise<HealthReportData>
    )

    const { data: noHistoryData, isLoading: noHistoryLoading, hasError: noHistoryError, refetch: refetchNoHistory } = useCachedFetch(
        'no-history-report',
        () => newHttpService.get('/api/backoffice/no-history-report') as Promise<NoHistoryData>
    )

    const { data: alertData, isLoading: alertsLoading, hasError: alertsError } = useCachedFetch(
        'alert-events',
        () => newHttpService.get('/api/backoffice/alert-events') as Promise<any[]>
    )

    const { data: gqlNames } = useCachedFetch(
        'gql-systems-entities',
        () => newHttpService.graphql('auditech', `{ systems { name abbreviation } entities { name abbreviation } }`) as Promise<{ systems: { name: string; abbreviation: string | null }[]; entities: { name: string; abbreviation: string | null }[] }>
    )
    const [healthRefreshing, setHealthRefreshing] = useState(false)
    const [noHistoryRefreshing, setNoHistoryRefreshing] = useState(false)

    const refreshHealth = useCallback(async () => {
        if (healthRefreshing) return
        setHealthRefreshing(true)
        try {
            await newHttpService.post('/api/backoffice/health-report/refresh')
            await refetchHealth()
        } finally {
            setHealthRefreshing(false)
        }
    }, [healthRefreshing, refetchHealth])

    const refreshNoHistory = useCallback(async () => {
        if (noHistoryRefreshing) return
        setNoHistoryRefreshing(true)
        try {
            await newHttpService.post('/api/backoffice/no-history-report/refresh')
            await refetchNoHistory()
        } finally {
            setNoHistoryRefreshing(false)
        }
    }, [noHistoryRefreshing, refetchNoHistory])

    const { systemNameMap, entityNameMap } = useMemo<{ systemNameMap: Record<string, string>; entityNameMap: Record<string, string> }>(() => {
        const sMap: Record<string, string> = {}
        const eMap: Record<string, string> = {}

        const addSys = (name: string) => {
            const lc = name.toLowerCase()
            sMap[lc] = name
            sMap[lc.replace(/\s+/g, '-')] = name
            sMap[lc.replace(/[\s\-_]+/g, '')] = name
        }

        // Health report data (ORM-cased, partial coverage)
        ;(healthData?.items || []).forEach(i => {
            if (i.entity_name) eMap[i.entity_name.toLowerCase()] = i.entity_name
            if (i.system_name) addSys(i.system_name)
        })
        ;(healthData?.error_findings || []).forEach(i => {
            if (i.entity_name) eMap[i.entity_name.toLowerCase()] = i.entity_name
            if (i.system_name) addSys(i.system_name)
        })
        ;(noHistoryData?.items || []).forEach(i => {
            if (i.entity_name) eMap[i.entity_name.toLowerCase()] = i.entity_name
            if (i.system_name) addSys(i.system_name)
        })

        // GraphQL data: complete list of all entities and systems with DB-accurate casing
        ;(gqlNames?.systems || []).forEach(s => {
            if (s.name) addSys(s.name)
            if (s.name && s.abbreviation) sMap[s.abbreviation.toLowerCase()] = s.name
        })
        ;(gqlNames?.entities || []).forEach(e => {
            if (e.name) eMap[e.name.toLowerCase()] = e.name
            if (e.name && e.abbreviation) eMap[e.abbreviation.toLowerCase()] = e.name
        })

        return { systemNameMap: sMap, entityNameMap: eMap }
    }, [healthData, noHistoryData, gqlNames])

    return (
        <div className="tablePageContainer">
            <div className="hr-page">
                {/* ── Summary cards ── */}
                <div className="hr-summary-row">
                    <HealthReportCard data={healthData} isLoading={healthLoading} hasError={healthError} isRefreshing={healthRefreshing} onRefresh={refreshHealth} />
                    <ErrorFindingsCard data={healthData} isLoading={healthLoading} hasError={healthError} isRefreshing={healthRefreshing} onRefresh={refreshHealth} />
                    <NoHistoryCard data={noHistoryData} isLoading={noHistoryLoading} hasError={noHistoryError} isRefreshing={noHistoryRefreshing} onRefresh={refreshNoHistory} />
                </div>

                <div className="hr-alerts-section">
                    <div className="hr-alerts-header">
                        <div className="hr-alerts-title-row">
                            <span className="hr-alerts-icon" aria-hidden="true">🚨</span>
                            <span className="hr-alerts-title">Alert Events</span>
                        </div>
                    </div>
                    {alertsLoading && <Spinner />}
                    {!alertsLoading && !alertsError && (
                        <AlertEventFeed data={alertData || []} highlightPair={highlightPair} systemNameMap={systemNameMap} entityNameMap={entityNameMap} />
                    )}
                    {!alertsLoading && alertsError && (
                        <div className="error-hint">Failed to load alert events. Try refreshing.</div>
                    )}
                </div>
            </div>
        </div>
    )
}
